import { zodResolver } from '@hookform/resolvers/zod';
import {
  type CreateCustomerPdfAwardRequest,
  CreateCustomerPdfAwardRequestSchema,
  type CustomerPdfAward,
  type UpdateCustomerPdfAwardRequest,
  UpdateCustomerPdfAwardRequestSchema,
} from '@utazzo/shared';
import { UzButton, UzDrawer, UzForm, UzFormField, UzInput, UzNumberInput } from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateAward, useUpdateAward } from '../use-customer-pdf';

import { MediaDropField } from '@/features/website-cms/components/MediaDropField';

interface AwardFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  award?: CustomerPdfAward | null;
}

/**
 * Create/edit drawer for an "Awards & Partners" award card: certificate
 * image + title (e.g. 'Abu Dhabi Travel Awards - Valued Partner (2023)').
 */
export const AwardFormDrawer = ({
  open,
  onOpenChange,
  mode,
  award,
}: AwardFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createMut = useCreateAward();
  const updateMut = useUpdateAward();
  const pending = createMut.isPending || updateMut.isPending;

  const createForm = useForm<CreateCustomerPdfAwardRequest>({
    resolver: zodResolver(CreateCustomerPdfAwardRequestSchema),
    defaultValues: { title: '', imageId: null, sortOrder: 0 },
  });
  const updateForm = useForm<UpdateCustomerPdfAwardRequest>({
    resolver: zodResolver(UpdateCustomerPdfAwardRequestSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset({ title: '', imageId: null, sortOrder: 0 });
    } else if (award) {
      updateForm.reset({
        title: award.title,
        imageId: award.imageId,
        sortOrder: award.sortOrder,
      });
    }
  }, [open, isCreate, award, createForm, updateForm]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const form = (isCreate ? createForm : updateForm) as ReturnType<typeof useForm<any>>;

  const onCreate = createForm.handleSubmit(async (values) => {
    try {
      await createMut.mutateAsync(values);
      onOpenChange(false);
    } catch {
      /* toast already fired in mutation hook */
    }
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!award) return;
    try {
      await updateMut.mutateAsync({ id: award.id, payload: values });
      onOpenChange(false);
    } catch {
      /* toast already fired */
    }
  });

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add award' : `Edit award`}
      description='Shown in the "Awards & Partners" section of the customer page.'
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Add award' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      <UzForm form={form} onSubmit={isCreate ? onCreate : onUpdate}>
        <UzFormField name="imageId">
          {({ field }) => (
            <MediaDropField
              value={field.value ?? null}
              onChange={(id) => field.onChange(id)}
              label="Award image"
              folder="customer-pdf"
              variant="square"
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name="title">
          {({ field, error }) => (
            <UzInput
              {...field}
              label="Title"
              placeholder="Abu Dhabi Travel Awards - Valued Partner (2023)"
              required
              disabled={pending}
              error={error}
            />
          )}
        </UzFormField>
        <UzFormField name="sortOrder">
          {({ field, error }) => (
            <UzNumberInput
              name={field.name}
              ref={field.ref}
              onBlur={field.onBlur}
              label="Sort order"
              value={field.value ?? 0}
              onValueChange={(v) => field.onChange(v ?? 0)}
              min={0}
              max={9999}
              step={10}
              hint="Lower values appear first."
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
      </UzForm>
    </UzDrawer>
  );
};
